import { ScreenHeader } from "@/components/common/ScreenHeader";
import { DesktopAppFrame } from "@/components/desktop/DesktopChrome";
import { ThemedText } from "@/components/themed-text";
import { Loader } from "@/components/ui/loader";
import { BrandColors, Colors } from "@/constants/theme";
import { useBooking } from "@/hooks/queries/use-bookings-query";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import {
  useAcceptQuote,
  useCounterQuote,
  useQuotes,
  useRejectQuote,
} from "@/hooks/queries/use-quotes-query";
import { showAlert } from "@/lib/alert";
import { isCustomerRole, isDriverRole } from "@/lib/roles";
import { locationTrackingService } from "@/lib/services/location-tracking";
import { Quote } from "@/lib/services/quotes";
import { showToast } from "@/lib/toast";
import { getAtServiceCharge, money } from "@/lib/utils";
import { useAuthStore, useUser } from "@/stores/use-auth-store";
import { usePendingOffersStore } from "@/stores/use-pending-offers-store";
import Ionicons from "@react-native-vector-icons/ionicons";
import { Stack, useLocalSearchParams, useRouter } from "expo-router";
import React, { useState } from "react";
import {
  ActivityIndicator,
  Modal,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
  useColorScheme,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

const getQuoteStatusBadge = (status: Quote["status"]) => {
  switch (status) {
    case "accepted":
      return {
        label: "Accepted",
        color: "#16A34A",
        backgroundColor: "#DCFCE7",
      };
    case "rejected":
      return {
        label: "Rejected",
        color: "#DC2626",
        backgroundColor: "#FEE2E2",
      };
    case "expired":
      return {
        label: "Expired",
        color: "#6B7280",
        backgroundColor: "#F3F4F6",
      };
    case "countered":
      return {
        label: "Countered",
        color: "#5856D6",
        backgroundColor: "#EDE9FE",
      };
    case "pending":
    default:
      return {
        label: "Pending",
        color: "#EA580C",
        backgroundColor: "#FFEDD5",
      };
  }
};

const COUNTER_INCREMENT = 5;

const quoteSortRank = (quote: Quote): number =>
  quote.status === "pending" || quote.status === "countered" ? 0 : 1;

export default function QuotesRoute() {
  return (
    <DesktopAppFrame>
      <QuotesScreen />
    </DesktopAppFrame>
  );
}

function QuotesScreen() {
  const { id } = useLocalSearchParams();
  const router = useRouter();
  const colorScheme = useColorScheme();
  const theme = Colors[colorScheme ?? "light"];
  const desktop = useDesktopLayout();
  const user = useUser();
  const isCustomer = isCustomerRole(user?.role);
  const isDriver = isDriverRole(user?.role);

  const { data, isLoading } = useQuotes({ booking_id: Number(id) });
  const { data: booking } = useBooking(Number(id), {
    enabled: isDriver && Number(id) > 0,
  });
  const platformFee = isDriver ? getAtServiceCharge(booking?.pricing) : 0;
  const isDriverPricingPending = isDriver && !booking;
  const toDisplayAmount = (amount: number) =>
    Math.max(0, Number(amount) - platformFee);
  const quotes: Quote[] = [...(data?.data ?? [])].sort((left, right) => {
    const rankDiff = quoteSortRank(left) - quoteSortRank(right);
    if (rankDiff !== 0) {
      return rankDiff;
    }

    return Number(left.amount) - Number(right.amount);
  });

  const acceptMutation = useAcceptQuote();
  const rejectMutation = useRejectQuote();
  const counterMutation = useCounterQuote();
  const [counterTarget, setCounterTarget] = useState<Quote | null>(null);
  const [counterAmount, setCounterAmount] = useState("");
  const currentCounterAmount = parseFloat(counterAmount) || 0;
  const canDecrementCounter = currentCounterAmount > COUNTER_INCREMENT;

  const adjustCounterAmount = (delta: number) => {
    const next = Math.max(0, currentCounterAmount + delta);
    setCounterAmount(next.toFixed(2));
  };

  const handleAcceptQuote = async (quote: Quote) => {
    const quoteId = quote.id;
    const confirmMessage = isDriver
      ? `You will earn ${money(toDisplayAmount(quote.amount))}. Are you sure you want to accept this quote?`
      : "Are you sure you want to accept this quote?";

    showAlert("Accept Quote", confirmMessage, [
      { text: "Cancel", style: "cancel" },
      {
        text: "Accept",
        onPress: async () => {
          try {
            await acceptMutation.mutateAsync(quoteId);
            showToast.success("Quote accepted successfully");

            if (!isDriver) {
              router.replace(`/bookings/${id}/live-tracking`);
              return;
            }

            const bookingId = Number(id);
            usePendingOffersStore.getState().removeOffersForBooking(bookingId);
            useAuthStore.getState().setActiveBooking(bookingId);
            router.replace(`/bookings/${id}/live-driving`);

            void (async () => {
              try {
                if (user?.driver && !user.driver.is_online) {
                  await locationTrackingService.setOnlineStatus(true);
                  useAuthStore.getState().setUser({
                    ...user,
                    driver: { ...user.driver, is_online: true },
                  });
                }

                await locationTrackingService.startTracking(
                  undefined,
                  bookingId,
                );
              } catch (trackingError) {
                console.error(
                  "Failed to start location tracking after accept:",
                  trackingError,
                );
              }
            })();
          } catch (error: any) {
            showToast.error(
              error.response?.data?.message || "Failed to accept quote",
            );
          }
        },
      },
    ]);
  };

  const handleRejectQuote = async (quoteId: number) => {
    showAlert("Reject Quote", "Are you sure you want to reject this quote?", [
      { text: "Cancel", style: "cancel" },
      {
        text: "Reject",
        style: "destructive",
        onPress: async () => {
          try {
            await rejectMutation.mutateAsync(quoteId);
            showToast.success("Quote rejected successfully");
          } catch (error: any) {
            showToast.error(
              error.response?.data?.message || "Failed to reject quote",
            );
          }
        },
      },
    ]);
  };

  const handleCounterQuote = async () => {
    if (!counterTarget || isDriverPricingPending) {
      return;
    }

    const amount = Number(counterAmount);
    if (!counterAmount || Number.isNaN(amount) || amount <= 0) {
      showToast.error("Enter a valid counter amount");
      return;
    }

    try {
      await counterMutation.mutateAsync({
        id: counterTarget.id,
        amount: Math.round((amount + platformFee) * 100) / 100,
      });
      setCounterTarget(null);
      setCounterAmount("");
      showToast.success("Counter-offer sent");
    } catch (error: any) {
      showToast.error(
        error.response?.data?.message || "Failed to send counter-offer",
      );
    }
  };

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container} edges={["top", "bottom"]}>
        <Stack.Screen
          options={{
            title: "",
            headerShown: !desktop,
            headerBackTitle: "Back",
            headerTintColor: theme.text,
          }}
        />
        {desktop && <ScreenHeader title="" showMenu={false} />}
        <Loader />
      </SafeAreaView>
    );
  }

  if (isCustomer || isDriver) {
    return (
      <SafeAreaView
        style={[styles.container, { backgroundColor: theme.background }]}
        edges={["bottom"]}
      >
        <Stack.Screen
          options={{
            title: "",
            headerShown: !desktop,
            headerBackTitle: "Back",
            headerShadowVisible: false,
            headerTintColor: theme.text,
          }}
        />
        {desktop && <ScreenHeader title="" showMenu={false} />}
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={[
            styles.scrollContent,
            desktop && styles.desktopContent,
          ]}
        >
          {quotes.length > 0 ? (
            <View style={styles.section}>
              {quotes.length > 0 && (
                <ThemedText style={styles.sectionTitle} type="defaultSemiBold">
                  Quotes ({quotes.length})
                </ThemedText>
              )}
              {quotes.map((quote) => {
                const statusBadge = getQuoteStatusBadge(quote.status);
                const showAccept = isCustomer
                  ? quote.status === "pending" || quote.can_accept
                  : quote.can_accept;
                const showCounter = quote.can_counter;
                const showReject = isCustomer
                  ? quote.status === "pending" || quote.can_accept
                  : quote.can_accept;

                return (
                  <View
                    key={quote.id}
                    style={[
                      styles.quoteCard,
                      {
                        backgroundColor: theme.background,
                        borderColor: theme.border,
                      },
                    ]}
                  >
                    <View style={styles.quoteHeader}>
                      <View style={styles.quoteHeaderLeft}>
                        <View style={styles.driverNameRow}>
                          <ThemedText
                            style={[styles.driverName, { color: theme.text }]}
                            type="defaultSemiBold"
                          >
                            {quote.driver?.name}
                          </ThemedText>
                          <View
                            style={[
                              styles.statusBadge,
                              { backgroundColor: statusBadge.backgroundColor },
                            ]}
                          >
                            <View
                              style={[
                                styles.statusDot,
                                { backgroundColor: statusBadge.color },
                              ]}
                            />
                            <ThemedText
                              style={[
                                styles.statusText,
                                { color: statusBadge.color },
                              ]}
                              type="extraSmallSemiBold"
                            >
                              {statusBadge.label}
                            </ThemedText>
                          </View>
                        </View>
                        <View style={styles.driverRating}>
                          <Ionicons name="star" size={16} color="#FF9500" />
                          <ThemedText style={styles.ratingText} type="small">
                            {quote.driver?.reputation_score || "N/A"}
                          </ThemedText>
                          <ThemedText
                            style={styles.tripsText}
                            type="extraSmall"
                          >
                            ({quote.driver?.total_ratings_count || 0} ratings)
                          </ThemedText>
                        </View>
                      </View>
                      <ThemedText
                        style={[styles.quoteAmount, { color: theme.primary }]}
                        type="defaultSemiBold"
                      >
                        {money(toDisplayAmount(quote.amount))}
                      </ThemedText>
                    </View>
                    {quote.notes && (
                      <ThemedText style={styles.quoteMessage} type="small">
                        {quote.notes}
                      </ThemedText>
                    )}
                    <View style={styles.quoteMeta}>
                      {quote.distance_miles && (
                        <ThemedText
                          style={styles.quoteMetaText}
                          type="extraSmall"
                        >
                          {quote.distance_miles} miles
                        </ThemedText>
                      )}
                      {quote.expires_at && (
                        <ThemedText
                          style={styles.quoteMetaText}
                          type="extraSmall"
                        >
                          Expires: {new Date(quote.expires_at).toLocaleString()}
                        </ThemedText>
                      )}
                    </View>
                    {(showAccept || showCounter || showReject) && (
                      <View style={styles.quoteActions}>
                        {showAccept && (
                          <TouchableOpacity
                            style={[
                              styles.quoteButton,
                              styles.acceptButton,
                              isDriverPricingPending && styles.buttonDisabled,
                            ]}
                            onPress={() => handleAcceptQuote(quote)}
                            disabled={isDriverPricingPending}
                          >
                            <ThemedText
                              style={styles.acceptButtonText}
                              type="smallSemiBold"
                            >
                              Accept
                            </ThemedText>
                          </TouchableOpacity>
                        )}
                        {showCounter && (
                          <TouchableOpacity
                            style={[
                              styles.quoteButton,
                              styles.counterButton,
                              isDriverPricingPending && styles.buttonDisabled,
                            ]}
                            disabled={isDriverPricingPending}
                            onPress={() => {
                              setCounterTarget(quote);
                              setCounterAmount(
                                toDisplayAmount(quote.amount).toFixed(2),
                              );
                            }}
                          >
                            <ThemedText
                              style={styles.counterButtonText}
                              type="smallSemiBold"
                            >
                              Counter
                            </ThemedText>
                          </TouchableOpacity>
                        )}
                        {showReject && (
                          <TouchableOpacity
                            style={[styles.quoteButton, styles.rejectButton]}
                            onPress={() => handleRejectQuote(quote.id)}
                          >
                            <ThemedText
                              style={styles.rejectButtonText}
                              type="smallSemiBold"
                            >
                              Reject
                            </ThemedText>
                          </TouchableOpacity>
                        )}
                      </View>
                    )}
                  </View>
                );
              })}
            </View>
          ) : (
            <View style={styles.content}>
              <ThemedText type="defaultSemiBold">No quotes found</ThemedText>
            </View>
          )}
        </ScrollView>
        <Modal
          visible={counterTarget !== null}
          transparent
          animationType="fade"
          onRequestClose={() => setCounterTarget(null)}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.modalCard}>
              <View style={styles.modalHeader}>
                <View style={styles.modalHeaderText}>
                  <ThemedText style={styles.modalTitle} type="defaultSemiBold">
                    Counter once
                  </ThemedText>
                  <ThemedText style={styles.modalHint} type="small">
                    {isDriver
                      ? "Send one price."
                      : "Send one price. The operator can reply once."}
                  </ThemedText>
                  {counterTarget ? (
                    <ThemedText style={styles.modalHint} type="small">
                      Current offer{" "}
                      <ThemedText
                        style={styles.modalCurrentPrice}
                        type="smallSemiBold"
                      >
                        {money(toDisplayAmount(counterTarget.amount))}
                      </ThemedText>
                    </ThemedText>
                  ) : null}
                </View>
                <TouchableOpacity
                  style={styles.modalCloseButton}
                  onPress={() => setCounterTarget(null)}
                  hitSlop={8}
                  activeOpacity={0.7}
                >
                  <Ionicons
                    name="close"
                    size={18}
                    color={BrandColors.charcoal}
                  />
                </TouchableOpacity>
              </View>

              <View style={styles.stepper}>
                <View style={styles.stepperControls}>
                  <TouchableOpacity
                    style={[
                      styles.stepperButton,
                      !canDecrementCounter && styles.stepperButtonDisabled,
                    ]}
                    onPress={() => adjustCounterAmount(-COUNTER_INCREMENT)}
                    disabled={!canDecrementCounter}
                    activeOpacity={0.7}
                  >
                    <Ionicons
                      name="remove"
                      size={22}
                      color={
                        canDecrementCounter
                          ? BrandColors.charcoal
                          : BrandColors.lightGray
                      }
                    />
                  </TouchableOpacity>

                  <View style={styles.priceInputRow}>
                    <ThemedText
                      style={styles.priceCurrency}
                      type="defaultSemiBold"
                    >
                      £
                    </ThemedText>
                    <TextInput
                      value={counterAmount}
                      onChangeText={setCounterAmount}
                      keyboardType="decimal-pad"
                      placeholder="0.00"
                      placeholderTextColor={BrandColors.lightGray}
                      style={styles.priceInput}
                      accessibilityLabel="counter-amount-input"
                    />
                  </View>

                  <TouchableOpacity
                    style={styles.stepperButton}
                    onPress={() => adjustCounterAmount(COUNTER_INCREMENT)}
                    activeOpacity={0.7}
                  >
                    <Ionicons
                      name="add"
                      size={22}
                      color={BrandColors.charcoal}
                    />
                  </TouchableOpacity>
                </View>
              </View>

              <View style={styles.quoteActions}>
                <TouchableOpacity
                  style={styles.modalCancelButton}
                  onPress={() => setCounterTarget(null)}
                  activeOpacity={0.7}
                >
                  <ThemedText
                    style={styles.modalCancelButtonText}
                    type="smallSemiBold"
                  >
                    Cancel
                  </ThemedText>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.modalSubmitButton}
                  onPress={handleCounterQuote}
                  disabled={counterMutation.isPending}
                  activeOpacity={0.8}
                >
                  {counterMutation.isPending ? (
                    <ActivityIndicator color={BrandColors.white} />
                  ) : (
                    <ThemedText
                      style={styles.modalSubmitButtonText}
                      type="smallSemiBold"
                    >
                      Send counter
                    </ThemedText>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>
      </SafeAreaView>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 40,
  },
  desktopContent: {
    width: "100%",
    maxWidth: 720,
    alignSelf: "center",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  bookingRef: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#1a1a1a",
    marginBottom: 4,
  },
  section: {
    padding: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 16,
  },
  quoteCard: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
  },
  quoteHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 12,
  },
  quoteHeaderLeft: {
    flex: 1,
    marginRight: 12,
  },
  driverNameRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
  },
  driverName: {
    fontSize: 16,
    fontWeight: "bold",
  },
  statusBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  statusText: {
    fontSize: 11,
    fontWeight: "600",
  },
  driverRating: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 4,
  },
  ratingText: {
    fontSize: 14,
    marginLeft: 4,
    fontWeight: "500",
  },
  tripsText: {
    fontSize: 12,
    color: "#8E8E93",
    marginLeft: 4,
  },
  quoteAmount: {
    fontSize: 18,
    fontWeight: "bold",
  },
  quoteMessage: {
    fontSize: 14,
    color: "#3A3A3C",
    marginBottom: 12,
    lineHeight: 20,
  },
  quoteMeta: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  quoteMetaText: {
    fontSize: 12,
    color: "#8E8E93",
  },
  quoteActions: {
    flexDirection: "row",
    gap: 12,
  },
  quoteButton: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 8,
    alignItems: "center",
  },
  acceptButton: {
    backgroundColor: "#007AFF",
  },
  acceptButtonText: {
    color: "#fff",
    fontWeight: "600",
  },
  rejectButton: {
    backgroundColor: "#F2F2F7",
  },
  rejectButtonText: {
    color: "#FF3B30",
    fontWeight: "600",
  },
  counterButton: {
    backgroundColor: "#EDE9FE",
  },
  counterButtonText: {
    color: "#5856D6",
    fontWeight: "600",
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  modalOverlay: {
    flex: 1,
    justifyContent: "center",
    padding: 24,
    backgroundColor: "rgba(0, 0, 0, 0.45)",
  },
  modalCard: {
    width: "100%",
    backgroundColor: BrandColors.offWhite,
    borderRadius: 20,
    padding: 24,
    gap: 24,
  },
  modalHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  modalHeaderText: {
    flex: 1,
    gap: 6,
  },
  modalTitle: {
    color: BrandColors.charcoal,
    fontSize: 22,
    fontWeight: "700",
  },
  modalHint: {
    color: BrandColors.mediumGray,
  },
  modalCurrentPrice: {
    color: BrandColors.primary,
    fontWeight: "700",
  },
  modalCloseButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: BrandColors.backgroundGray,
    alignItems: "center",
    justifyContent: "center",
  },
  stepper: {
    borderWidth: 1,
    borderColor: BrandColors.lightGray,
    borderRadius: 16,
    paddingHorizontal: 16,
    paddingVertical: 20,
  },
  stepperControls: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  stepperButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: BrandColors.backgroundGray,
    alignItems: "center",
    justifyContent: "center",
  },
  stepperButtonDisabled: {
    backgroundColor: BrandColors.offWhite,
  },
  priceInputRow: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  priceCurrency: {
    fontSize: 32,
    lineHeight: 38,
    fontWeight: "700",
    color: BrandColors.secondary,
  },
  priceInput: {
    minWidth: 60,
    padding: 0,
    fontSize: 32,
    lineHeight: 38,
    fontWeight: "700",
    color: BrandColors.secondary,
    textAlign: "center",
  },
  modalCancelButton: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 14,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: BrandColors.lightGray,
    backgroundColor: BrandColors.white,
  },
  modalCancelButtonText: {
    color: BrandColors.charcoal,
    fontSize: 15,
    fontWeight: "600",
  },
  modalSubmitButton: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 14,
    borderRadius: 14,
    backgroundColor: BrandColors.primary,
  },
  modalSubmitButtonText: {
    color: BrandColors.white,
    fontSize: 15,
    fontWeight: "600",
  },
  actionButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#fff",
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: "#E5E5EA",
  },
  cancelButton: {
    backgroundColor: "#FF3B30",
    borderColor: "#FF3B30",
  },
  // Modern Driver Search Styles
  headerContainer: {
    paddingHorizontal: 20,
    paddingTop: 10,
    paddingBottom: 20,
  },
  mainTitle: {
    fontSize: 28,
    fontWeight: "700",
    marginBottom: 8,
  },
  mainSubtitle: {
    fontSize: 16,
    lineHeight: 22,
  },
  spinner: {
    marginTop: 20,
    alignSelf: "center",
  },
  statusCard: {
    borderRadius: 20,
    marginHorizontal: 20,
    padding: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 4,
  },
  matchingBanner: {
    backgroundColor: "#1D9B91",
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 14,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  matchingText: {
    fontSize: 14,
  },
  matchingProgressContainer: {
    flex: 1,
    height: 6,
    backgroundColor: "rgba(255,255,255,0.3)",
    borderRadius: 3,
    marginHorizontal: 12,
    overflow: "hidden",
  },
  matchingProgressBar: {
    height: "100%",
    backgroundColor: "#fff",
    borderRadius: 3,
  },
  timerText: {
    fontSize: 14,
  },
  statusList: {
    marginBottom: 20,
  },
  statusItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    paddingVertical: 12,
  },
  statusIcon: {
    marginRight: 12,
    marginTop: 2,
  },
  statusTextContainer: {
    flex: 1,
  },
  statusTitle: {
    fontSize: 16,
    marginBottom: 4,
  },
  statusDescription: {
    fontSize: 14,
  },
  statusDescriptionBlue: {
    fontSize: 14,
    lineHeight: 20,
  },
  statusListDivider: {
    height: 1,
    marginLeft: 36,
  },
  actionButtons: {
    gap: 12,
  },
  notifyButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 14,
    paddingVertical: 14,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
    gap: 8,
  },
  notifyMeText: {
    color: "#111827",
    fontSize: 16,
  },
  cancelRequestButton: {
    marginBottom: 0,
  },
  notifyingCard: {
    backgroundColor: "#fff",
    borderRadius: 16,
    padding: 24,
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#E5E5EA",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 10,
    elevation: 2,
    gap: 12,
  },
  notifyingTitle: {
    fontSize: 20,
    fontWeight: "700",
    marginBottom: 4,
  },
  notifyingSubtitle: {
    fontSize: 16,
    textAlign: "center",
  },
  notifyingTextContainer: {
    alignItems: "center",
  },
  searchPromptCard: {
    borderRadius: 16,
    padding: 20,
    borderWidth: 1,
    gap: 12,
  },
  searchPromptContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    marginBottom: 20,
  },
  searchPromptText: {
    flex: 1,
  },
  notifyButtonLarge: {
    backgroundColor: BrandColors.primary,
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: "center",
  },
  notifyButtonText: {
    color: "#fff",
    fontSize: 14,
    fontWeight: "700",
  },
  button: {
    height: 50,
    width: "100%",
    borderRadius: 14,
    backgroundColor: "#E5E7EB",
    overflow: "hidden",
    justifyContent: "center",
  },
  progress: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: BrandColors.primary,
  },
  content: {
    alignItems: "center",
    justifyContent: "center",
  },
  text: {
    color: "#FFFFFF",
    fontWeight: "600",
    fontSize: 16,
  },
});
